import React, { useEffect, useState } from "react";
import baseUrl from "../../../../api/baseUrl";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import MedicationIcon from "@mui/icons-material/Medication";

type FormDataType = {
  drugs: string;
  dose: string;
  doses: string[];
  type: string;
};

function AddDrugs({ close, id }: any) {
  const initialFormData: FormDataType = {
    drugs: "",
    dose: "",
    doses: [""],
    type: "",
  };
  const [formData, setformData] = useState(initialFormData);

  const onchange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name.startsWith("doses/")) {
      const index = Number(name.split("/")[1]);
      setformData((prevState) => ({
        ...prevState,
        doses: prevState.doses.map((dose, i) => (i === index ? value : dose)),
      }));
    } else {
      setformData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  useEffect(() => {
    if (id) {
      setformData((prev) => ({
        ...prev,
        doses: id.dose,
        drugs: id.drugs,
        type: id.type,
      }));
    } else {
      setformData(initialFormData);
    }
  }, [id]);

  const add = () => {
    setformData((prevState) => ({
      ...prevState,
      doses: [...prevState.doses, ""],
    }));
  };

  const removeDoseByIndex = (indexToRemove: number) => {
    setformData((prevState) => ({
      ...prevState,
      doses: prevState.doses.filter((_, index) => index !== indexToRemove),
    }));
  };

  const onsubmit = (e: any) => {
    e.preventDefault();
    if (id === "") {
      baseUrl
        .post("/client/prescription/prescription/createPrescription", { ...formData })
        .then((response) => {
          close(false);
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      baseUrl
        .patch(
          "/client/prescription/prescription/updatePrescriptionById",
          { ...formData },
          { headers: { prescriptionid: id?._id } }
        )
        .then((response) => {
          close(false);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MedicationIcon className="text-white" sx={{ fontSize: 24 }} />
            <h2 className="text-lg font-bold text-white">
              {id === "" ? "Add Drug" : "Update Drug"}
            </h2>
          </div>
          <button
            onClick={() => close(false)}
            className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          {/* Drug Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Drug Name
            </label>
            <input
              onChange={onchange}
              value={formData.drugs}
              name="drugs"
              type="text"
              placeholder="Enter drug name"
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
            />
          </div>

          {/* Doses */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-slate-700">Doses</label>
              <button
                type="button"
                onClick={() => add()}
                className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-emerald-600 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors"
              >
                <AddIcon sx={{ fontSize: 16 }} />
                Add Dose
              </button>
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {formData?.doses?.map((value, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    value={value}
                    name={`doses/${index}`}
                    onChange={(e) => onchange(e)}
                    placeholder={`Dose ${index + 1}`}
                    className="flex-1 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all text-sm"
                  />
                  {formData.doses.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeDoseByIndex(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Type</label>
            <select
              onChange={onchange}
              value={formData.type}
              name="type"
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
            >
              <option value="">Select type</option>
              <option value="mg">mg</option>
              <option value="ml">ml</option>
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
          <button
            onClick={() => close(false)}
            className="px-5 py-2.5 text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={(e) => onsubmit(e)}
            className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl hover:shadow-lg hover:shadow-emerald-200 transition-all font-medium"
          >
            {id === "" ? "Submit" : "Update"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddDrugs;
